import { NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  Audience,
  AutomationActionDenormalized,
  AutomationActionTemplate,
  AutomationActionType,
  IAutomationTemplate,
  IExperienceTemplateResource,
  ITimelineItem,
  SubmitTargetType
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { useCurrentUser } from '../../../../../services/currentUser';
import { useUserRuleConfigWithAutocompletion } from '../../../../../services/user';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@material-ui/core';
import arrayMove from 'array-move';
import { mapValues, sortBy } from 'lodash';
import {
  Calendar,
  Clock,
  Copy,
  Eye,
  EyeOff,
  FilePen,
  GripVertical,
  PauseCircle,
  PlayCircle,
  Trash2,
  UserCheck,
  X,
  Zap
} from 'lucide-react';
import * as UX from '../newux/kit';
import { useNewUx } from '../../../../../services/newUx';
import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable
} from 'react-beautiful-dnd';
import { ErrorBoundary } from 'react-error-boundary';
import shortid from 'shortid';
import { CustomInputLabel } from '../../../../../components/CustomInputLabel';
import { DebugText } from '../../../../../components/DebugText';
import { DeleteButton } from '../../../../../components/DeletionConfirmation';
import { DeactivateItemConfirmDialog } from '../../DeactivateItemButton';
import { DialogTitleWithTypography } from '../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../components/InfoBox';
import { LinkExternal } from '../../../../../components/LinkExternal';
import {
  PlainTextWithReplacementPlaceholders,
  TokenDefinition
} from '../../../../../components/MarkdownText';
import { TextFieldWithReplacementTokens } from '../../../../../components/TextFieldWithReplacementTokens';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../i18next';
import { useRoutes } from '../../../../../routes';
import {
  clearEveryoneNameIfSole,
  getAudienceById,
  getAudienceName,
  getEveryoneAudience
} from '../../../../../services/experiences/audience';
import { LightMdToJsx } from '../../../../../services/formatter';
import { INTEGRATION_DESCRIPTIONS } from '../../../../Org/pages/Integrations/integrations/constants';
import { AnchorField } from '../../Anchors';
import {
  CONTROLS_CONTAINER_CLASS_NAME,
  DragDropItemContainer,
  DragDropItemControlsButton,
  DragDropItemControlsContainer,
  DragDropItemFlag,
  DragDropItemFlagsContainer,
  DragDropListContainer,
  DragDropListEmpty
} from '../components/DragDropList';
import {
  NewUxFullWidthPanel,
  NewUxFullWidthPanelBody,
  NewUxFullWidthPanelHead,
  NewUxViewerFullWidth,
  SideSettingsReadOnlyNoSelection,
  ViewerField,
  ViewerSegmentList
} from '../TaskBuilder';
import {
  AutomationSettingsAudiencesDetails,
  AutomationSettingsAudiencesOverview,
  DEFAULT_AUDIENCE_COLOR,
  TaskSettingsOverviewSectionAudiences
} from '../TaskBuilder/components/Audiences';
import { BlockAudienceAssign } from '../TaskBuilder/components/Messages';
import {
  AutomationSettingsOverviewSectionTrigger,
  AutomationSettingsTriggerV2,
  AutomationViewerTrigger
} from '../TaskBuilder/components/AutomationTrigger';
import {
  Grid,
  SettingsBody,
  SettingsFooter,
  SettingsSection,
  SIDE_CONTAINER_MAX_WIDTH,
  SIDE_CONTAINER_MIN_WIDTH
} from '../TaskBuilder/components/layout';
import { SideContainerHeader } from '../TaskBuilder/components/SideContainerHeader';
import {
  AUTOMATION_BUILDER_VIEW_MODES,
  AutomationBuilderViewMode,
  BlockSettingsSubPage,
  MinimalTaskBuilderContext,
  TaskBuilderContext,
  ViewModeAudiencesDetail
} from '../TaskBuilder/types';
import {
  getActionDefKey,
  useActionDefinition,
  useActionDefinitions
} from './actionHelpers';
import {
  ActionSettingsAsanaCreateTask,
  ActionSettingsAsanaCreateTaskReadOnly
} from './ActionSettings/Asana';
import {
  ActionSettingsAtlassianCreateIssue,
  ActionSettingsAtlassianCreateIssueReadOnly
} from './ActionSettings/Atlassian';
import {
  ActionSettingsCreateDocumentFromTemplate,
  ActionSettingsCreateDocumentFromTemplateReadOnly,
  CreateDocumentProviderType
} from './ActionSettings/CreateDocument';
import {
  ActionSettingsEnrollInExperience,
  ActionSettingsEnrollInExperienceReadOnly
} from './ActionSettings/EnrollInExperience';
import {
  ActionSettingsGoogleCalendarEventInvitation,
  ActionSettingsGoogleCalendarEventInvitationReadOnly,
  useGoogleCalendarEventInvitiationAnchorOptions
} from './ActionSettings/GoogleCalendar';
import {
  ActionSettingsGoogleAddToGroup,
  ActionSettingsGoogleAddToGroupReadOnly
} from './ActionSettings/GoogleGroup';
import {
  ActionSettingsHttpPost,
  ActionSettingsHttpPostReadOnly
} from './ActionSettings/HttpPost';
import {
  ActionSettingsDoceboEnroll,
  ActionSettingsDoceboEnrollReadOnly
} from './ActionSettings/Docebo';
import {
  ActionSettingsSyncToDataDestination,
  ActionSettingsSyncToDataDestinationReadOnly
} from './ActionSettings/SyncToDataDestination';
import {
  ActionSettingsWorkday,
  ActionSettingsWorkdayReadOnly
} from './ActionSettings/Workday';
import {
  ActionSettingsLessonlyAssignToContent,
  ActionSettingsLessonlyAssignToContentReadOnly
} from './ActionSettings/Lessonly';
import {
  ActionSettingsMicrosoftCalendarEventInvitation,
  ActionSettingsMicrosoftCalendarEventInvitationReadOnly
} from './ActionSettings/MicrosoftCalendar';
import {
  ActionSettingsMicrosoftAddToGroup,
  ActionSettingsMicrosoftAddToGroupReadOnly
} from './ActionSettings/MicrosoftGroup';
import {
  ActionSettingsMicrosoftTeamsInviteToChannel,
  ActionSettingsMicrosoftTeamsInviteToChannelReadOnly
} from './ActionSettings/MicrosoftTeams';
import {
  ActionSettingsOktaAddToGroup,
  ActionSettingsOktaAddToGroupReadOnly
} from './ActionSettings/OktaGroup';
import {
  ActionSettingsSlackInviteToChannel,
  ActionSettingsSlackInviteToChannelReadOnly
} from './ActionSettings/Slack';
import { ActionSummaryAsanaCreateTask } from './ActionSummary/Asana';
import { ActionSummaryAtlassianCreateTask } from './ActionSummary/Atlassian';
import { ActionSummaryCreateDocumentFromTemplate } from './ActionSummary/CreateDocument';
import { ActionSummaryEnrollInExperience } from './ActionSummary/EnrollInExperience';
import { ActionSummaryGoogleCalendarEventInvitation } from './ActionSummary/GoogleCalendar';
import { ActionSummaryGoogleGroupAdd } from './ActionSummary/GoogleGroup';
import { ActionSummaryDoceboEnroll } from './ActionSummary/Docebo';
import { ActionSummaryHttpPost } from './ActionSummary/HttpPost';
import { ActionSummarySyncToDataDestination } from './ActionSummary/SyncToDataDestination';
import { ActionSummaryWorkday } from './ActionSummary/Workday';
import { ActionSummaryLessonlyAssignToContent } from './ActionSummary/Lessonly';
import { ActionSummaryMicrosoftCalendarEventInvitation } from './ActionSummary/MicrosoftCalendar';
import { ActionSummaryMicrosoftGroupAdd } from './ActionSummary/MicrosoftGroup';
import { ActionSummaryMicrosoftTeamsInviteToChannel } from './ActionSummary/MicrosoftTeamsInviteToChannel';
import { ActionSummaryOktaGroupAdd } from './ActionSummary/OktaGroup';
import {
  ActionSummaryBody,
  ActionSummaryContainer,
  ActionSummaryHeading
} from './ActionSummary/shared';
import { ActionSummarySlackInviteToChannel } from './ActionSummary/Slack';
import { ActionDefinition, AutomationActionKey } from './types';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  validateAction
} from './validation';

// Like `assertNever`, this keeps compile-time exhaustiveness (a genuinely new
// action type still errors here in code) — but at RUNTIME it returns null
// instead of throwing, so malformed AI-generated automation data degrades
// gracefully rather than crashing the whole builder.
const neverRender = (_value: never): null => null;

const SideContainer = styled('div')((p) => ({
  padding: `${p.theme.spacing(4)}px ${p.theme.spacing(3)}px`,
  height: '100%',
  width: '100%',
  overflow: 'auto'
}));

const MainContainer = styled(SideContainer)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  borderRight: p.theme.custom.border.standard,
  borderLeft: p.theme.custom.border.standard,
  backgroundColor: p.theme.custom.colors.gray.w100
}));

/*
@guide Create a new automation action - 4.1 Add the Settings component

pkgs/app/src/features/Experiences/components/Builders/AutomationBuilder/ActionSettings/

*/
const ActionSettingsInner = ({
  value,
  automation,
  onChange,
  resources,
  onChangeResources,
  expTpl,
  validation,
  setValidation,
  anchorFields,
  tokens,
  customFields,
  goToSubPage,
  showOnCompleteHookSettings
}: {
  value: AutomationActionTemplate;
  automation: IAutomationTemplate;
  onChange: (nextValue: AutomationActionTemplate) => void;
  expTpl: TaskBuilderContext;
  validation: AutomationBuilderActionValidationStateItem | null;
  resources: IExperienceTemplateResource[];
  customFields: CustomUserField[];
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
}) => {
  switch (value.type) {
    case AutomationActionType.ASANA_CREATE_TASK:
      return (
        <ActionSettingsAsanaCreateTask
          value={value}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
        />
      );
    case AutomationActionType.ATLASSIAN_CREATE_ISSUE:
      return (
        <ActionSettingsAtlassianCreateIssue
          value={value}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
          tokens={tokens}
        />
      );
    case AutomationActionType.ENROLL_IN_EXPERIENCE:
      return (
        <ActionSettingsEnrollInExperience
          value={value}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
        />
      );
    case AutomationActionType.GOOGLE_ADD_TO_GROUP:
      return (
        <ActionSettingsGoogleAddToGroup
          value={value}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
        />
      );
    case AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION:
      return (
        <ActionSettingsGoogleCalendarEventInvitation
          value={value}
          automation={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
          anchorFields={anchorFields}
        />
      );
    case AutomationActionType.DOCEBO_ENROLL:
      return (
        <ActionSettingsDoceboEnroll
          value={value}
          automation={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
          anchorFields={anchorFields}
        />
      );
    case AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT:
      return (
        <ActionSettingsLessonlyAssignToContent
          value={value}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
        />
      );
    case AutomationActionType.SLACK_INVITE_TO_CHANNEL:
      return (
        <ActionSettingsSlackInviteToChannel
          value={value}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
          tokens={tokens}
        />
      );
    case AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION:
      return (
        <ActionSettingsMicrosoftCalendarEventInvitation
          value={value}
          automation={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
          anchorFields={anchorFields}
        />
      );
    case AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP:
      return (
        <ActionSettingsMicrosoftAddToGroup
          value={value}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
        />
      );
    case AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL:
      return (
        <ActionSettingsMicrosoftTeamsInviteToChannel
          value={value}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
          tokens={tokens}
        />
      );

    case AutomationActionType.OKTA_ADD_TO_GROUP:
      return (
        <ActionSettingsOktaAddToGroup
          value={value}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
        />
      );

    case AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE:
      return (
        <ActionSettingsCreateDocumentFromTemplate
          providerType={
            value.target.type === SubmitTargetType.GOOGLE_DRIVE
              ? CreateDocumentProviderType.GOOGLE
              : CreateDocumentProviderType.MICROSOFT
          }
          value={value}
          tokens={tokens}
          aTpl={automation}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
          resources={resources}
          onChangeResources={onChangeResources}
          showOnCompleteHookSettings={showOnCompleteHookSettings}
          customFields={customFields}
          goToSubPage={goToSubPage}
        />
      );
    case AutomationActionType.UNENROLL_FROM_EXPERIENCE:
      return null;
    case AutomationActionType.HTTP_POST:
      return (
        <ActionSettingsHttpPost
          value={value}
          aTpl={automation}
          tokens={tokens}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          setValidation={setValidation}
        />
      );
    case AutomationActionType.SYNC_TO_DATA_DESTINATION:
      return (
        <ActionSettingsSyncToDataDestination
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          validation={validation}
          resources={resources}
          onChangeResources={onChangeResources}
          customFields={customFields}
        />
      );
    case AutomationActionType.WORKDAY_TRIGGER_BUSINESS_PROCESS:
      return (
        <ActionSettingsWorkday
          value={value}
          onChange={onChange}
          customFields={customFields}
          orgId={expTpl.orgId}
          validation={validation}
        />
      );
    default:
      return neverRender(value);
  }
};

// NEW_UX: pad the action-config panel like the other inspector panels and
// restyle the shared MUI inputs/selects the 15 action forms use into the light
// te-cfg design language (prototype ActionConfigPanel) — one wrapper instead of
// editing every form. Classic path is untouched (only used under newUx).
const NewUxActionConfig = styled('div')`
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 20px;

  .MuiOutlinedInput-root {
    border-radius: 8px;
    background: ${UX.tokens.color.white};
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${UX.tokens.color.borderSubtle};
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${UX.tokens.color.n300};
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${UX.tokens.color.blue500};
    border-width: 1.5px;
  }
  .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
    border-color: ${UX.tokens.color.red600};
  }
  .MuiOutlinedInput-input,
  .MuiSelect-select {
    padding: 11px 12px;
    font-size: 16px;
    font-weight: ${UX.tokens.font.weightScale.regular};
    color: ${UX.tokens.color.black};
  }
  .MuiOutlinedInput-input::placeholder {
    font-size: 16px;
    font-weight: ${UX.tokens.font.weightScale.regular};
    color: ${UX.tokens.color.grey700};
    opacity: 1;
  }
  .MuiInputLabel-outlined {
    font-size: 13px;
  }
  .MuiFormHelperText-root {
    font-size: 11px;
    font-weight: 300;
    margin-left: 2px;
  }
`;

const ActionSettings = ({
  a,
  automation,
  onChange,
  resources,
  onChangeResources,
  onBack,
  expTpl,
  validation,
  setValidation,
  anchorFields,
  tokens,
  customFields,
  goToSubPage,
  showOnCompleteHookSettings,
  newUx = false
}: {
  a: AutomationActionTemplate;
  automation: IAutomationTemplate;
  onChange: (nextAction: AutomationActionTemplate) => void;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  onBack: () => void;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  newUx?: boolean;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
}) => {
  const def = useActionDefinition(a);
  return (
    <>
      <SideContainerHeader def={def} onBack={onBack} newUx={newUx} />
      <ActionSettingsInner
        value={a}
        automation={automation}
        onChange={onChange}
        expTpl={expTpl}
        validation={validation}
        resources={resources}
        onChangeResources={onChangeResources}
        setValidation={setValidation}
        anchorFields={anchorFields}
        tokens={tokens}
        showOnCompleteHookSettings={showOnCompleteHookSettings}
        customFields={customFields}
        goToSubPage={goToSubPage}
      />
      <DebugText>Action ID: {a.id}</DebugText>
    </>
  );
};

const DROP_IDS = {
  actionsSelectorContent: 'droppable-action-selector-content',
  actionOrder: 'droppable-action-order'
};

const BlockListItem = styled('div')<{ disabled?: boolean }>((p) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  padding: `${p.theme.spacing(1)}px ${p.theme.spacing(1.5)}px`,

  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  cursor: p.disabled ? 'inherit' : 'grab',
  gap: p.theme.spacing(),

  '> *': {
    opacity: p.disabled ? 0.3 : 1
  }
}));

const BlockListDragClone = styled('div')((p) => ({
  transform: 'none !important'
}));

const BlockListContainer = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing()
}));

const IGNORED_ACTION_TYPES: AutomationActionType[] = [
  AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL
];
const ActionSelector = () => {
  const defs = useActionDefinitions();
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();
  // TODO - empty state - invite people to add integrations,
  // if there are no defs around at all.
  const filteredDefs = useMemo(() => {
    return defs.filter((d) => !IGNORED_ACTION_TYPES.includes(d.type));
  }, [defs]);
  return (
    <SideContainer>
      <Droppable
        droppableId={DROP_IDS.actionsSelectorContent}
        isDropDisabled={true}
      >
        {(provided) => (
          <BlockListContainer
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {filteredDefs.map((def, i) => (
              <Draggable
                key={def.key}
                draggableId={def.key}
                index={i}
                isDragDisabled={!def.enabled}
              >
                {(provided, snapshot) => {
                  const item = def.enabled ? (
                    <BlockListItem>
                      <img alt={''} width={16} height={16} src={def.iconSrc} />
                      <div>{def.label}</div>
                    </BlockListItem>
                  ) : (
                    <LinkExternal href={ROUTES.org.integrations.url()}>
                      <Tooltip
                        classes={{
                          tooltip: css({ maxWidth: 'none !important' })
                        }}
                        title={
                          def.key ===
                            AutomationActionKey.CREATE_DOCUMENT_FROM_TEMPLATE_GOOGLE &&
                          !def.enabled &&
                          def.parentIntegration
                            ? `${t(
                                'Please re-set up your Google Integration to use this action',
                                {
                                  name:
                                    INTEGRATION_DESCRIPTIONS[
                                      def.parentIntegration
                                    ].name
                                }
                              )}`
                            : def.enabled || !def.parentIntegration
                            ? ''
                            : `${t(
                                'Please activate the {{ name }} integration to use this action',
                                {
                                  name:
                                    INTEGRATION_DESCRIPTIONS[
                                      def.parentIntegration
                                    ].name
                                }
                              )}`
                        }
                      >
                        <BlockListItem disabled={!def.enabled}>
                          <img
                            alt={''}
                            width={16}
                            height={16}
                            src={def.iconSrc}
                          />
                          <div>{def.label}</div>
                        </BlockListItem>
                      </Tooltip>
                    </LinkExternal>
                  );
                  return (
                    <>
                      <div
                        key={def.key}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          transform: snapshot.isDragging
                            ? provided.draggableProps.style?.transform
                            : 'translate(0px, 0px)'
                        }}
                      >
                        {item}
                      </div>
                      {snapshot.isDragging && (
                        <BlockListDragClone>{item}</BlockListDragClone>
                      )}
                    </>
                  );
                }}
              </Draggable>
            ))}
          </BlockListContainer>
        )}
      </Droppable>
    </SideContainer>
  );
};

const ActionDeleteConfirmationDialog = ({
  open,
  onClose,
  onConfirm
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithTypography>
        {t('Delete action?')}
      </DialogTitleWithTypography>
      <DialogContent>
        {t(`The action will be removed immediately. This can't be undone.`)}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Back to editing')}
        </Button>
        <Button color="secondary" variant="contained" onClick={onConfirm}>
          {t('Delete action')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ActionItem = ({
  a,
  selected,
  error,
  dragging,
  disabled,
  onSelectAction,
  onDeleteAction,
  onDuplicateAction,
  onToggleActionEnabled,
  flags,
  children,
  newUx,
  dragHandleProps,
  assignAudiences,
  onToggleActionAudience,
  focusedAudienceId
}: {
  a: AutomationActionTemplate;
  selected: boolean;
  error: boolean;
  dragging: boolean;
  disabled?: boolean;
  onSelectAction?: (nextSelected: string | null) => void;
  onDeleteAction?: (toDelete: string) => void;
  onDuplicateAction?: (actionId: string) => void;
  onToggleActionEnabled?: (enabled: boolean) => void;
  flags?: React.ReactNode[];
  children: React.ReactNode;
  newUx?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  assignAudiences?: Audience[];
  onToggleActionAudience?: (actionId: string, audienceId: string) => void;
  // NEW_UX: the segment currently previewed (null on the "All"/"Everyone" tab). When
  // set, Delete offers "for all" vs "just for this segment" like the content blocks.
  focusedAudienceId?: string | null;
}) => {
  const { t } = useUnsafeTranslation();
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const [
    deleteMenuAnchor,
    setDeleteMenuAnchor
  ] = React.useState<HTMLElement | null>(null);
  // NEW_UX: on a segment tab, Delete is a two-option menu (whole action vs this
  // segment); elsewhere it's a single delete → confirmation dialog.
  const segmentScopedDelete = !!(
    newUx &&
    focusedAudienceId &&
    onToggleActionAudience
  );

  return (
    <>
      <DragDropItemContainer
        role="button"
        onClick={
          onSelectAction
            ? (ev) => {
                // stopPropagation works in conjunction with a click handler on the outside,
                // which deselects items on click.
                // we need this guard to make the items itself clickable
                ev.stopPropagation();
                !selected && onSelectAction(a.id);
              }
            : undefined
        }
        selected={selected}
        error={error}
        dragging={dragging}
        newUx={newUx}
      >
        <div style={{ pointerEvents: 'none' }}>{children}</div>
        {(onToggleActionEnabled ||
          onDeleteAction ||
          (newUx && assignAudiences && onToggleActionAudience)) && (
          <>
            <DragDropItemControlsContainer
              className={CONTROLS_CONTAINER_CLASS_NAME}
              newUx={newUx}
            >
              {newUx && assignAudiences && onToggleActionAudience && (
                <BlockAudienceAssign
                  blockId={a.id}
                  audiences={assignAudiences}
                  onToggle={onToggleActionAudience}
                  newUx={newUx}
                />
              )}
              {onToggleActionEnabled && (
                <DragDropItemControlsButton
                  newUx={newUx}
                  onClick={() => {
                    return onToggleActionEnabled(!!disabled);
                  }}
                >
                  {disabled && (
                    <>
                      <Eye size={12} /> <div>{t('Show block')}</div>
                    </>
                  )}
                  {!disabled && (
                    <>
                      <EyeOff size={12} /> <div>{t('Hide block')}</div>
                    </>
                  )}
                </DragDropItemControlsButton>
              )}
              {onDuplicateAction && (
                <DragDropItemControlsButton
                  newUx={newUx}
                  onClick={() => onDuplicateAction(a.id)}
                >
                  <Copy size={12} /> <div>{t('Duplicate')}</div>
                </DragDropItemControlsButton>
              )}
              {newUx && dragHandleProps && (
                <DragDropItemControlsButton
                  newUx={newUx}
                  {...dragHandleProps}
                  aria-label={t('Reorder')}
                  title={t('Reorder')}
                  style={{ cursor: 'grab' }}
                >
                  <GripVertical size={12} /> <div>{t('Reorder')}</div>
                </DragDropItemControlsButton>
              )}
              {onDeleteAction && (
                <DragDropItemControlsButton
                  newUx={newUx}
                  onClick={
                    segmentScopedDelete
                      ? (ev) => setDeleteMenuAnchor(ev.currentTarget)
                      : openDialog
                  }
                >
                  <Trash2 size={12} /> <div>{t('Delete')}</div>
                </DragDropItemControlsButton>
              )}
            </DragDropItemControlsContainer>
            {onDeleteAction && segmentScopedDelete && (
              <Menu
                anchorEl={deleteMenuAnchor}
                open={!!deleteMenuAnchor}
                onClose={() => setDeleteMenuAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem
                  onClick={() => {
                    setDeleteMenuAnchor(null);
                    openDialog();
                  }}
                >
                  {t('Delete for all')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setDeleteMenuAnchor(null);
                    onToggleActionAudience?.(a.id, focusedAudienceId!);
                  }}
                >
                  {t('Delete just for this segment')}
                </MenuItem>
              </Menu>
            )}
            {onDeleteAction && (
              <ActionDeleteConfirmationDialog
                open={dialogOpen}
                onClose={closeDialog}
                onConfirm={() => onDeleteAction(a.id)}
              />
            )}
          </>
        )}

        {flags && (
          <DragDropItemFlagsContainer>{flags}</DragDropItemFlagsContainer>
        )}
      </DragDropItemContainer>
    </>
  );
};

/*
@guide Create a new automation action - 4.2 Add the Summary component

Shown in the Preview section of the AutomationBuilder
*/
export const ActionSummary = ({
  a,
  expTpl,
  def,
  anchorFields,
  currentTemplateId
}: {
  a: AutomationActionTemplate | AutomationActionDenormalized;
  expTpl: MinimalTaskBuilderContext;
  def: ActionDefinition;
  anchorFields: AnchorField[];
  currentTemplateId?: string;
}) => {
  if (a.type === AutomationActionType.ASANA_CREATE_TASK) {
    return <ActionSummaryAsanaCreateTask a={a} expTpl={expTpl} def={def} />;
  }

  if (a.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE) {
    return <ActionSummaryAtlassianCreateTask a={a} expTpl={expTpl} def={def} />;
  }

  if (a.type === AutomationActionType.ENROLL_IN_EXPERIENCE) {
    return <ActionSummaryEnrollInExperience a={a} expTpl={expTpl} def={def} currentTemplateId={currentTemplateId} />;
  }

  if (a.type === AutomationActionType.GOOGLE_ADD_TO_GROUP) {
    return <ActionSummaryGoogleGroupAdd a={a} expTpl={expTpl} def={def} />;
  }

  if (a.type === AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION) {
    return (
      <ActionSummaryGoogleCalendarEventInvitation
        a={a}
        expTpl={expTpl}
        def={def}
        anchorFields={anchorFields}
      />
    );
  }

  if (a.type === AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION) {
    return (
      <ActionSummaryMicrosoftCalendarEventInvitation
        a={a}
        expTpl={expTpl}
        def={def}
        anchorFields={anchorFields}
      />
    );
  }

  if (a.type === AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP) {
    return <ActionSummaryMicrosoftGroupAdd a={a} expTpl={expTpl} def={def} />;
  }

  if (a.type === AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL) {
    return (
      <ActionSummaryMicrosoftTeamsInviteToChannel
        a={a}
        expTpl={expTpl}
        def={def}
      />
    );
  }

  if (a.type === AutomationActionType.OKTA_ADD_TO_GROUP) {
    return <ActionSummaryOktaGroupAdd a={a} expTpl={expTpl} def={def} />;
  }

  if (a.type === AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT) {
    return (
      <ActionSummaryLessonlyAssignToContent a={a} expTpl={expTpl} def={def} />
    );
  }

  if (a.type === AutomationActionType.SLACK_INVITE_TO_CHANNEL) {
    return (
      <ActionSummarySlackInviteToChannel a={a} expTpl={expTpl} def={def} />
    );
  }

  if (a.type === AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE) {
    return (
      <ActionSummaryCreateDocumentFromTemplate
        a={a}
        expTpl={expTpl}
        def={def}
      />
    );
  }

  if (a.type === AutomationActionType.DOCEBO_ENROLL) {
    return <ActionSummaryDoceboEnroll a={a} expTpl={expTpl} def={def} />;
  }

  if (a.type === AutomationActionType.HTTP_POST) {
    return <ActionSummaryHttpPost a={a} expTpl={expTpl} def={def} />;
  }

  if (a.type === AutomationActionType.SYNC_TO_DATA_DESTINATION) {
    return (
      <ActionSummarySyncToDataDestination a={a} expTpl={expTpl} def={def} />
    );
  }

  if (a.type === AutomationActionType.WORKDAY_TRIGGER_BUSINESS_PROCESS) {
    return <ActionSummaryWorkday a={a} expTpl={expTpl} def={def} />;
  }

  if (a.type === AutomationActionType.UNENROLL_FROM_EXPERIENCE) {
    return (
      <ActionSummaryContainer>
        <ActionSummaryHeading def={def} />
        <ActionSummaryBody>
          <LightMdToJsx>
            {'Unenroll **Participant** from this experience.'}
          </LightMdToJsx>
        </ActionSummaryBody>
      </ActionSummaryContainer>
    );
  }

  console.warn('UNKNOWN ACTION TYPE', a);
  return neverRender(a);
};

const ActionListContainer = styled('div')((p) => ({
  flex: 1
}));

const ActionsArea = styled('div')`
  height: 100%;
`;

const InfoBoxContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const ActionList = ({
  value,
  expTpl,

  selectedAction,
  onSelectAction,
  onDeleteAction,
  onDuplicateAction,
  draggable,
  viewMode,
  dragDropContext,
  onPickActions,
  validation,
  anchorFields,
  currentTemplateId,
  newUx,
  onToggleActionAudience
}: {
  value: IAutomationTemplate;
  expTpl: MinimalTaskBuilderContext;

  selectedAction: string | null;
  onSelectAction?: (nextSelected: string | null) => void;
  onDeleteAction?: (actionId: string) => void;
  onDuplicateAction?: (actionId: string) => void;
  draggable: boolean;
  viewMode: AutomationBuilderViewMode;
  dragDropContext?: React.MutableRefObject<{
    currentlyVisibleActions: string[];
  }>;
  onPickActions?: (blocks: string[]) => void;
  validation: AutomationBuilderActionValidationState;
  anchorFields: AnchorField[];
  currentTemplateId?: string;
  newUx?: boolean;
  onToggleActionAudience?: (actionId: string, audienceId: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { actions, audiences } = value;
  const defs = useActionDefinitions();

  const audience = useMemo(() => {
    if (viewMode.mode === 'AUDIENCES_DETAIL') {
      return viewMode.editModel;
    }
    if (viewMode.mode === 'AUDIENCES_OVERVIEW' && viewMode.previewAudienceId) {
      return getAudienceById(audiences, viewMode.previewAudienceId);
    }
    return getEveryoneAudience(audiences);
  }, [viewMode, audiences]);

  const infoBox = (
    <>
      {viewMode.mode === 'AUDIENCES_OVERVIEW' &&
      viewMode.previewAudienceId &&
      audience.blocks.length === 0 ? (
        <InfoBox>
          <LightMdToJsx>
            {t(
              "No visible actions. This automation won't be sent to the **{{ audienceName }}** audience.",
              {
                audienceName: getAudienceName(audience, audiences.length > 1, t)
              }
            )}
          </LightMdToJsx>
        </InfoBox>
      ) : viewMode.mode === 'AUDIENCES_OVERVIEW' &&
        viewMode.previewAudienceId ? (
        // NEW_UX: the selected segment is already shown by the segment tabs on top
        // of the preview sheet, so the "This is a preview of X" banner is redundant.
        newUx ? null : (
          <InfoBox>
            <LightMdToJsx>
              {t('This is a preview of the **{{ audienceName }}** audience.', {
                audienceName: getAudienceName(audience, audiences.length > 1, t)
              })}
            </LightMdToJsx>
          </InfoBox>
        )
      ) : viewMode.mode === 'AUDIENCES_OVERVIEW' && audiences.length > 1 ? (
        <InfoBox>
          {t('Showing all actions. Select an audience to preview.')}
        </InfoBox>
      ) : viewMode.mode === 'AUDIENCES_DETAIL' ? (
        <InfoBox>
          <LightMdToJsx>
            {t(
              'New actions will only apply to the audience **{{ audienceName }}**.',
              {
                audienceName: getAudienceName(audience, audiences.length > 1, t)
              }
            )}
          </LightMdToJsx>
        </InfoBox>
      ) : null}
    </>
  );

  const totalNumberOfActions = actions.length;

  const flags = useMemo(() => {
    // NEW_UX: per-block audience flags ("E" / "All") are redundant with the segment
    // tabs and the per-block Assign control, so the new editor omits them.
    if (newUx) {
      return undefined;
    }
    if (audiences.length === 1) {
      if (audiences[0].blocks.length === totalNumberOfActions) {
        return undefined;
      }
    }

    if (viewMode.mode === 'AUDIENCES_OVERVIEW' && viewMode.previewAudienceId) {
      return undefined;
    }

    if (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks) {
      return undefined;
    }

    const res: { [blockId: string]: React.ReactNode[] } = {};
    audiences.forEach((uneditedAudience) => {
      const a =
        viewMode.mode === 'AUDIENCES_DETAIL' &&
        uneditedAudience.id === viewMode.editModel.id
          ? viewMode.editModel
          : uneditedAudience;
      const name = getAudienceName(a, audiences.length > 1, t);
      const el = (
        <DragDropItemFlag
          key={a.id}
          label={name[0]}
          color={a.color || DEFAULT_AUDIENCE_COLOR}
          tooltip={t('Used by {{ audienceName }} audience', {
            audienceName: name
          })}
        />
      );
      a.blocks.forEach((bId) => {
        const container = (res[bId] = res[bId] || []);
        container.push(el);
      });
    });
    return mapValues(res, (els) => {
      if (els.length === audiences.length && audiences.length > 1) {
        return [
          <DragDropItemFlag
            key="all"
            label={t('All')}
            color={DEFAULT_AUDIENCE_COLOR}
            tooltip={t('Used by all audiences')}
          />
        ];
      }
      return els;
    });
  }, [viewMode, audiences, totalNumberOfActions, t, newUx]);

  const ignoreAudience = !(
    (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks) ||
    (viewMode.mode === 'AUDIENCES_OVERVIEW' && viewMode.previewAudienceId)
  );

  const visibleActions = useMemo(
    () =>
      ignoreAudience
        ? actions
        : actions.filter((a) => audience.blocks.includes(a.id)),
    [actions, audience, ignoreAudience]
  );

  if (dragDropContext) {
    dragDropContext.current = {
      currentlyVisibleActions: visibleActions.map((a) => a.id)
    };
  }

  const actionPicker:
    | {
        picked: string[];
        onUpdate: (blocks: string[]) => void;
      }
    | undefined = useMemo(() => {
    if (onPickActions && viewMode.mode === 'AUDIENCES_DETAIL') {
      const audience = viewMode.editModel;
      return {
        picked: audience.blocks,
        onUpdate: onPickActions
      };
    }
    return undefined;
  }, [viewMode, onPickActions]);

  return (
    <ActionListContainer>
      {infoBox && <InfoBoxContainer>{infoBox}</InfoBoxContainer>}
      <ActionsArea onClick={() => onSelectAction?.(null)}>
        <Droppable droppableId={DROP_IDS.actionOrder} isDropDisabled={false}>
          {(provided, snapshot) => (
            <DragDropListContainer
              highlight={snapshot.isDraggingOver}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {visibleActions.map((a, i) => {
                const def = defs.find((d) => d.key === getActionDefKey(a));
                if (!def) {
                  console.log('ACTION DEFINITION NOT FOUND', {
                    type: a.type,
                    action: a,
                    defs
                  });
                  return null;
                }
                return (
                  <Draggable
                    key={a.id}
                    draggableId={a.id}
                    index={i}
                    isDragDisabled={!draggable}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...(newUx ? {} : provided.dragHandleProps)}
                      >
                        <ActionItem
                          a={a}
                          selected={selectedAction === a.id}
                          error={validation[a.id]?.error || false}
                          dragging={snapshot.isDragging}
                          newUx={newUx}
                          assignAudiences={audiences}
                          onToggleActionAudience={onToggleActionAudience}
                          focusedAudienceId={
                            audience && audience.rule !== 'EVERYONE'
                              ? audience.id
                              : null
                          }
                          dragHandleProps={
                            newUx ? provided.dragHandleProps : undefined
                          }
                          disabled={
                            !!(
                              actionPicker &&
                              !actionPicker.picked.includes(a.id)
                            )
                          }
                          onSelectAction={onSelectAction}
                          onDeleteAction={onDeleteAction}
                          onDuplicateAction={onDuplicateAction}
                          onToggleActionEnabled={
                            actionPicker
                              ? (nextEnabled) => {
                                  console.log('nextEnabled', nextEnabled);
                                  actionPicker.onUpdate(
                                    nextEnabled
                                      ? [...actionPicker.picked, a.id]
                                      : actionPicker.picked.filter(
                                          (x) => x !== a.id
                                        )
                                  );
                                }
                              : undefined
                          }
                          flags={flags?.[a.id]}
                        >
                          <ActionSummary
                            a={a}
                            expTpl={expTpl}
                            def={def}
                            anchorFields={anchorFields}
                            currentTemplateId={currentTemplateId}
                          />
                        </ActionItem>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {!actions.length && (
                <DragDropListEmpty>{t('Drag actions here')}</DragDropListEmpty>
              )}
              {!!actions.length && provided.placeholder}
            </DragDropListContainer>
          )}
        </Droppable>
      </ActionsArea>
    </ActionListContainer>
  );
};

const AutomationSettingsOverview = ({
  value,
  onChange,
  onDelete,
  expTpl,
  setViewMode,
  tokens,
  onGoToOtherItem
}: {
  value: IAutomationTemplate;
  onChange?: React.Dispatch<React.SetStateAction<IAutomationTemplate>>;
  onDelete?: ((t: IAutomationTemplate) => void) | undefined;
  expTpl: TaskBuilderContext;
  setViewMode?: (
    nextViewMode: Exclude<AutomationBuilderViewMode, ViewModeAudiencesDetail>
  ) => void;
  tokens: TokenDefinition[];
  onGoToOtherItem?: (nextItem: ITimelineItem) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <SideContainer>
      <SettingsBody>
        <SettingsSection>
          <FormRow>
            <CustomInputLabel
              label={t('Admin Name')}
              fullWidth
            >
              {onChange ? (
                <TextFieldWithReplacementTokens
                  fullWidth
                  variant="outlined"
                  value={value.title}
                  onChange={(nextTitle) =>
                    onChange((v) => ({ ...v, title: nextTitle }))
                  }
                  error={!value.title}
                  helperText={
                    value.title ? undefined : t('Please enter a title')
                  }
                  tokens={tokens}
                />
              ) : (
                <PlainTextWithReplacementPlaceholders
                  text={value.title}
                  tokens={tokens}
                />
              )}
            </CustomInputLabel>
          </FormRow>
        </SettingsSection>
        <AutomationSettingsOverviewSectionTrigger
          title={t('Trigger Date')}
          value={value}
          onEdit={
            setViewMode
              ? () => setViewMode(AUTOMATION_BUILDER_VIEW_MODES.trigger)
              : undefined
          }
          divider
          allTasks={expTpl.tasks}
          onGoToOtherItem={onGoToOtherItem}
        />
        <TaskSettingsOverviewSectionAudiences
          title={t('Target Audiences')}
          audiences={value.audiences}
          onEdit={
            setViewMode
              ? () =>
                  setViewMode(
                    AUTOMATION_BUILDER_VIEW_MODES.audiencesOverview(null)
                  )
              : undefined
          }
          divider
        />
      </SettingsBody>
      {onDelete && (
        <SettingsFooter>
          <DeleteButton
            variant="outlined"
            color="secondary"
            startIcon={<Trash2 size={20} />}
            dialogTitle={t('Delete Automation?')}
            buttonAction={t('Delete Automation')}
            onDelete={async () => onDelete(value)}
          >
            {t('Delete Automation')}
          </DeleteButton>
        </SettingsFooter>
      )}
    </SideContainer>
  );
};

const AutomationSettings = ({
  value,
  onChange,
  expTpl,
  onDelete,
  viewMode,
  setViewMode,
  customUserFields,
  tokens,
  onGoToOtherItem
}: {
  value: IAutomationTemplate;
  onChange: React.Dispatch<React.SetStateAction<IAutomationTemplate>>;
  onDelete?: (t: IAutomationTemplate) => void;
  expTpl: TaskBuilderContext;
  viewMode: AutomationBuilderViewMode;
  setViewMode: (nextViewMode: AutomationBuilderViewMode) => void;
  customUserFields: CustomUserField[];
  tokens: TokenDefinition[];
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const onBack = useCallback(
    () => setViewMode(AUTOMATION_BUILDER_VIEW_MODES.settingsOverview),
    [setViewMode]
  );

  switch (viewMode.mode) {
    case 'SETTINGS_OVERVIEW':
      return (
        <AutomationSettingsOverview
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onDelete={onDelete}
          setViewMode={setViewMode}
          tokens={tokens}
          onGoToOtherItem={onGoToOtherItem}
        />
      );
    case 'TRIGGER':
      return (
        <AutomationSettingsTriggerV2
          title={t('Trigger Date')}
          description={''}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
          onGoToOtherItem={onGoToOtherItem}
        />
      );

    case 'AUDIENCES_OVERVIEW':
      return (
        <AutomationSettingsAudiencesOverview
          title={t('Target Audiences')}
          description={t(
            'Use actions for certain participants based on their profile.'
          )}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
          previewAudienceId={viewMode.previewAudienceId}
          onPreview={(audienceId) =>
            setViewMode(
              AUTOMATION_BUILDER_VIEW_MODES.audiencesOverview(audienceId)
            )
          }
          goToDetails={(audience) =>
            setViewMode(
              AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(audience, false)
            )
          }
        />
      );
    case 'AUDIENCES_DETAIL':
      return (
        <AutomationSettingsAudiencesDetails
          title={t('Edit Target Audience')}
          description={''}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          model={viewMode.editModel}
          onUpdateModel={(nextModel) =>
            setViewMode(
              AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(
                nextModel,
                viewMode.showHiddenBlocks
              )
            )
          }
          showHiddenBlocks={viewMode.showHiddenBlocks}
          onToggleHiddenBlocks={() =>
            setViewMode(
              AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(
                viewMode.editModel,
                !viewMode.showHiddenBlocks
              )
            )
          }
          onBack={() => {
            return setViewMode(
              AUTOMATION_BUILDER_VIEW_MODES.audiencesOverview(
                viewMode.editModel.id
              )
            );
          }}
          customUserFields={customUserFields}
        />
      );
    default:
      return null;
  }
};

const findTrueInsertionIndex = (
  currentVisibleIds: string[],
  allIds: string[],
  requestedInsertionIndex: number
) => {
  if (currentVisibleIds.length === allIds.length) {
    return requestedInsertionIndex;
  }
  if (!allIds.length) {
    return 0;
  }

  if (requestedInsertionIndex === currentVisibleIds.length) {
    // insert behind the previous item
    const prevItem = currentVisibleIds[requestedInsertionIndex - 1];
    return allIds.indexOf(prevItem) + 1;
  }

  // If the first item is shown, put it at the top
  if (requestedInsertionIndex === 0 && currentVisibleIds[0] === allIds[0]) {
    return 0;
  }

  const currentItem = currentVisibleIds[requestedInsertionIndex];
  return allIds.indexOf(currentItem);
};

/**
 * NEW_UX automation editor shell — mirrors the task/message shells for automations.
 * Rail: Actions (palette / selected action settings) · Audience (segments) · Timing
 * (trigger + catch-up). No Delivery panel (automations have no channels/recipient).
 * Reuses ActionList / ActionSettings / AutomationSettingsTriggerV2 /
 * AutomationSettingsAudiences* + AutomationBuilder's own handlers. Gated behind newUx.
 */
type AutomationRail = 'actions' | 'audience' | 'timing' | 'notes';
type AutomationNewUxShellCtx = {
  value: IAutomationTemplate;
  onChange: React.Dispatch<SetStateAction<IAutomationTemplate>>;
  onDelete?: (t: IAutomationTemplate) => void;
  onDuplicate?: () => void;
  expTpl: TaskBuilderContext;
  tokens: TokenDefinition[];
  viewMode: AutomationBuilderViewMode;
  setViewMode: (v: AutomationBuilderViewMode) => void;
  selectedAction: AutomationActionTemplate | undefined;
  selectedActionId: string | null;
  selectActionId: (id: string | null) => void;
  deselectAction: () => void;
  onAddAction: (
    a: AutomationActionTemplate,
    index: number,
    restrictAudiences?: string[]
  ) => void;
  onDeleteAction: (id: string) => void;
  onChangeAction: (a: AutomationActionTemplate) => void;
  onDuplicateAction: (id: string) => void;
  onPickActions?: (ids: string[]) => void;
  actionDefs: ReturnType<typeof useActionDefinitions>;
  dragDropContext: React.MutableRefObject<{ currentlyVisibleActions: string[] }>;
  restrictAllActionOperations: boolean;
  restrictActionResorting: boolean;
  resources: IExperienceTemplateResource[];
  onChangeResources: (r: IExperienceTemplateResource[]) => void;
  validation: AutomationBuilderActionValidationState;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  anchorFields: AnchorField[];
  customFields: CustomUserField[];
  subPage: BlockSettingsSubPage | null;
  setSubPage: (s: BlockSettingsSubPage | null) => void;
  onBackFromSubPage: () => void;
  onGoToOtherItem?: (i: ITimelineItem) => void;
  newUxPrimaryAction?: React.ReactNode;
  /** Toggle deactivate/reactivate from the top-bar overflow menu (edit mode only). */
  newUxOnToggleDeactivate?: () => void;
  onCloseDialog?: () => void;
  notesPanel?: React.ReactNode;
};

const AutomationEditorNewUxShell = ({
  ctx
}: {
  ctx: AutomationNewUxShellCtx;
}) => {
  const { t } = useUnsafeTranslation();
  const [rail, setRail] = useState<AutomationRail>('actions');
  const [railCollapsed, setRailCollapsed] = useState(false);
  // Deactivate/Reactivate is confirmed before it persists (parity with classic).
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const {
    value,
    onChange,
    expTpl,
    tokens,
    viewMode,
    setViewMode,
    selectedAction,
    selectedActionId,
    selectActionId,
    actionDefs,
    subPage,
    setSubPage,
    onBackFromSubPage,
    onGoToOtherItem
  } = ctx;

  // Open (and expand) a rail without ever collapsing — used by the preview's
  // "+ Add segment" tab, which should only ever open the panel.
  const openRail = (next: AutomationRail) => {
    setRailCollapsed(false);
    setRail(next);
    selectActionId(null);
    setSubPage(null);
    if (next === 'audience') {
      if (
        viewMode.mode !== 'AUDIENCES_OVERVIEW' &&
        viewMode.mode !== 'AUDIENCES_DETAIL'
      ) {
        setViewMode(AUTOMATION_BUILDER_VIEW_MODES.audiencesOverview(null));
      }
    } else if (viewMode.mode !== 'SETTINGS_OVERVIEW') {
      setViewMode(AUTOMATION_BUILDER_VIEW_MODES.settingsOverview);
    }
  };

  const goRail = (next: AutomationRail) => {
    // Only the rail icons toggle: re-clicking the active (open) rail collapses it.
    if (next === rail && !railCollapsed) {
      setRailCollapsed(true);
      return;
    }
    openRail(next);
  };

  const addAction = (def: { emptyAction: () => AutomationActionTemplate }) => {
    if (ctx.restrictAllActionOperations) return;
    const restrictAudiences =
      viewMode.mode === 'AUDIENCES_DETAIL' ? [] : undefined;
    const na = def.emptyAction();
    ctx.onAddAction(na, value.actions.length, restrictAudiences);
    selectActionId(na.id);
  };

  const renderInspector = () => {
    // A selected action opens its settings over ANY rail (Actions, Audience,
    // Timing) — clicking a block in the preview always reveals its config.
    if (subPage) {
      return (
        <NewUxActionConfig>
          <SideContainerHeader
            def={subPage.header.def}
            onBack={onBackFromSubPage}
            newUx
          />
          {React.createElement(subPage.body, {
            onBack: onBackFromSubPage,
            resources: ctx.resources,
            onChangeResources: ctx.onChangeResources
          })}
        </NewUxActionConfig>
      );
    }
    if (selectedAction) {
      return (
        <NewUxActionConfig>
          <ActionSettings
            a={selectedAction}
            automation={value}
            onChange={ctx.onChangeAction}
            onBack={ctx.deselectAction}
            expTpl={expTpl}
            resources={ctx.resources}
            onChangeResources={ctx.onChangeResources}
            validation={ctx.validation[selectedAction.id] || null}
            setValidation={ctx.setValidation}
            anchorFields={ctx.anchorFields}
            tokens={tokens}
            customFields={ctx.customFields}
            goToSubPage={setSubPage}
            showOnCompleteHookSettings={true}
            newUx
          />
        </NewUxActionConfig>
      );
    }
    if (rail === 'timing') {
      return (
        <AutomationSettingsTriggerV2
          title={t('Trigger Date')}
          description={''}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={() => undefined}
          onGoToOtherItem={onGoToOtherItem}
          newUx
        />
      );
    }
    if (rail === 'audience') {
      return (
        <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
          <UX.PanelHeader
            icon={<UserCheck size={17} />}
            title={t('Audience')}
            hint={t('Which segments this automation targets.')}
          />
          <AutomationSettingsAudiencesOverview
            title={t('Target Audiences')}
            description={t(
              'Use actions for certain participants based on their profile.'
            )}
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            onBack={() => undefined}
            newUx
            embedded
            previewAudienceId={
              viewMode.mode === 'AUDIENCES_OVERVIEW'
                ? viewMode.previewAudienceId
                : null
            }
            onPreview={(audienceId) =>
              setViewMode(
                AUTOMATION_BUILDER_VIEW_MODES.audiencesOverview(audienceId)
              )
            }
            goToDetails={(audience) =>
              setViewMode(
                AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(audience, false)
              )
            }
            expandedAudienceId={
              viewMode.mode === 'AUDIENCES_DETAIL'
                ? viewMode.editModel.id
                : null
            }
            onToggleExpand={(id) => {
              if (id) {
                const aud = value.audiences.find((a) => a.id === id);
                if (aud) {
                  setViewMode(
                    AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(aud, false)
                  );
                }
              } else {
                setViewMode(
                  AUTOMATION_BUILDER_VIEW_MODES.audiencesOverview(null)
                );
              }
            }}
            inlineDetail={
              viewMode.mode === 'AUDIENCES_DETAIL' ? (
                <AutomationSettingsAudiencesDetails
                  title={t('Edit Target Audience')}
                  description={''}
                  value={value}
                  onChange={onChange}
                  expTpl={expTpl}
                  model={viewMode.editModel}
                  onUpdateModel={(nextModel) =>
                    setViewMode(
                      AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(
                        nextModel,
                        viewMode.showHiddenBlocks
                      )
                    )
                  }
                  showHiddenBlocks={viewMode.showHiddenBlocks}
                  onToggleHiddenBlocks={() =>
                    setViewMode(
                      AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(
                        viewMode.editModel,
                        !viewMode.showHiddenBlocks
                      )
                    )
                  }
                  onBack={() =>
                    setViewMode(
                      AUTOMATION_BUILDER_VIEW_MODES.audiencesOverview(
                        viewMode.editModel.id
                      )
                    )
                  }
                  customUserFields={ctx.customFields}
                  newUx
                  inline
                />
              ) : undefined
            }
          />
        </div>
      );
    }
    // actions (default: nothing selected)
    return (
      <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
        <UX.PanelHeader
          icon={<Zap size={17} />}
          title={t('Actions')}
          hint={t('Name the automation and add the actions it runs.')}
        />
        <UX.FieldLabel>{t('Admin Name')}</UX.FieldLabel>
        <TextFieldWithReplacementTokens
          value={value.title}
          fullWidth
          variant="outlined"
          onChange={(nextTitle) => onChange((v) => ({ ...v, title: nextTitle }))}
          error={!value.title}
          helperText={value.title ? undefined : t('Please enter a title')}
          tokens={tokens}
          compact
        />
        <UX.SectionLabel>{t('Add action')}</UX.SectionLabel>
        {/* Prototype ActionsPanel lists actions one per row (single column,
            editor: automation-app.jsx ActionsPanel `gridTemplateColumns:'1fr'`).
            We keep OUR action icons rather than the prototype's letter-badges. */}
        <UX.BlockPickerGrid style={{ gridTemplateColumns: '1fr' }}>
          {actionDefs
            .filter((d) => d.enabled)
            .map((def) => (
              <UX.BlockButton
                key={def.key}
                icon={
                  def.iconSrc ? (
                    <img alt="" width={16} height={16} src={def.iconSrc} />
                  ) : undefined
                }
                label={def.label}
                onClick={() => addAction(def)}
              />
            ))}
        </UX.BlockPickerGrid>
      </div>
    );
  };

  return (
    <UX.EditorShell>
      <UX.SectionRailContainer>
        <UX.RailButton active={rail === 'actions'} title={t('Actions')} onClick={() => goRail('actions')}>
          <Zap size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'audience'} title={t('Audience')} onClick={() => goRail('audience')}>
          <UserCheck size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'timing'} title={t('Timing & triggers')} onClick={() => goRail('timing')}>
          <Clock size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'notes'} title={t('Notes')} onClick={() => goRail('notes')}>
          <FilePen size={20} />
        </UX.RailButton>
      </UX.SectionRailContainer>
      <UX.RightColumn>
        <UX.TopBar>
          <UX.TopBarHeading>
            <UX.TopBarCrumb title={expTpl.title}>{expTpl.title}</UX.TopBarCrumb>
            <UX.TopBarTitle>
              <UX.TopBarTitleText title={value.title || t('Automation')}>
                {value.title || t('Automation')}
              </UX.TopBarTitleText>
              {ctx.newUxOnToggleDeactivate && (
                <UX.ActivePill active={!value.deactivated} />
              )}
            </UX.TopBarTitle>
          </UX.TopBarHeading>
          <UX.TopBarSpacer />
          <UX.TopBarRight>
            {ctx.newUxPrimaryAction}
            <UX.OverflowMenu
              items={[
                ...(ctx.onDuplicate
                  ? [
                      {
                        label: t('Duplicate'),
                        icon: <Copy size={15} />,
                        onClick: ctx.onDuplicate
                      }
                    ]
                  : []),
                ...(ctx.newUxOnToggleDeactivate
                  ? [
                      {
                        label: value.deactivated
                          ? t('Reactivate')
                          : t('Deactivate'),
                        icon: value.deactivated ? (
                          <PlayCircle size={15} />
                        ) : (
                          <PauseCircle size={15} />
                        ),
                        onClick: () => setDeactivateConfirmOpen(true)
                      }
                    ]
                  : []),
                ...(ctx.onDelete
                  ? [
                      {
                        label: t('Delete'),
                        icon: <Trash2 size={15} />,
                        onClick: () => ctx.onDelete?.(value),
                        danger: true
                      }
                    ]
                  : [])
              ]}
            />
            {ctx.onCloseDialog && (
              <IconButton size="small" aria-label={t('Close')} onClick={ctx.onCloseDialog}>
                <X size={18} />
              </IconButton>
            )}
          </UX.TopBarRight>
        </UX.TopBar>
        <UX.EditorBody>
          {rail === 'notes' ? (
            <NewUxViewerFullWidth>
              <NewUxFullWidthPanel>
                <NewUxFullWidthPanelHead>
                  <UX.PanelHeader icon={<FilePen size={17} />} title={t('Notes')} />
                </NewUxFullWidthPanelHead>
                <NewUxFullWidthPanelBody>{ctx.notesPanel}</NewUxFullWidthPanelBody>
              </NewUxFullWidthPanel>
            </NewUxViewerFullWidth>
          ) : (
            <>
          <NewUxAutomationColumn collapsed={railCollapsed}>
            {renderInspector()}
          </NewUxAutomationColumn>
          <UX.PreviewCanvas onBackgroundClick={() => selectActionId(null)}>
            <div style={{ width: '100%', maxWidth: 720, margin: '0 auto' }}>
              <UX.AudienceTabs
                allLabelEveryone={t('All – sends to everyone')}
                allLabelBlocks={t('All blocks')}
                everyone={{
                  id: getEveryoneAudience(value.audiences).id,
                  label: getAudienceName(getEveryoneAudience(value.audiences), true, t),
                  empty:
                    getEveryoneAudience(value.audiences).blocks.length === 0
                }}
                segments={value.audiences
                  .filter((a) => a.rule !== 'EVERYONE')
                  .map((a) => ({
                    id: a.id,
                    label: a.name || t('Segment'),
                    color: a.color || UX.tokens.color.blue500,
                    empty: a.blocks.length === 0
                  }))}
                activeId={
                  viewMode.mode === 'AUDIENCES_OVERVIEW'
                    ? viewMode.previewAudienceId
                    : null
                }
                onSelect={(id) =>
                  setViewMode(
                    id
                      ? AUTOMATION_BUILDER_VIEW_MODES.audiencesOverview(id)
                      : AUTOMATION_BUILDER_VIEW_MODES.settingsOverview
                  )
                }
                onAdd={() => openRail('audience')}
                onDelete={(id) => {
                  onChange((v) => ({
                    ...v,
                    audiences: clearEveryoneNameIfSole(
                      v.audiences.filter((a) => a.id !== id)
                    )
                  }));
                  setViewMode(AUTOMATION_BUILDER_VIEW_MODES.settingsOverview);
                }}
              />
              <ActionList
                value={value}
                expTpl={expTpl}
                selectedAction={selectedActionId}
                onSelectAction={
                  ctx.restrictAllActionOperations ? undefined : selectActionId
                }
                onDeleteAction={
                  ctx.restrictAllActionOperations ? undefined : ctx.onDeleteAction
                }
                validation={ctx.validation}
                anchorFields={ctx.anchorFields}
                draggable={!ctx.restrictActionResorting}
                viewMode={viewMode}
                dragDropContext={ctx.dragDropContext}
                onPickActions={ctx.onPickActions}
                onDuplicateAction={ctx.onDuplicateAction}
                currentTemplateId={expTpl.templateId}
                newUx
                onToggleActionAudience={(actionId, audienceId) =>
                  onChange((v) => ({
                    ...v,
                    audiences: v.audiences.map((a) =>
                      a.id === audienceId
                        ? {
                            ...a,
                            blocks: a.blocks.includes(actionId)
                              ? a.blocks.filter((x) => x !== actionId)
                              : [...a.blocks, actionId]
                          }
                        : a
                    )
                  }))
                }
              />
            </div>
          </UX.PreviewCanvas>
            </>
          )}
        </UX.EditorBody>
      </UX.RightColumn>
      <DeactivateItemConfirmDialog
        open={deactivateConfirmOpen}
        deactivated={value.deactivated}
        onClose={() => setDeactivateConfirmOpen(false)}
        onConfirm={() => ctx.newUxOnToggleDeactivate?.()}
      />
    </UX.EditorShell>
  );
};

const NewUxAutomationColumn = styled('div')<{ collapsed?: boolean }>`
  width: ${(p) => (p.collapsed ? '0px' : '500px')};
  flex: 0 0 ${(p) => (p.collapsed ? '0px' : '500px')};
  border-right: ${(p) =>
    p.collapsed ? 'none' : `1px solid ${UX.tokens.color.borderSubtle}`};
  opacity: ${(p) => (p.collapsed ? 0 : 1)};
  overflow: hidden;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  transition: width 0.18s ease, opacity 0.15s ease;
`;

const AutomationBuilderInner = ({
  value,
  onChange,
  onDelete,
  expTpl,
  validation,
  resources,
  onChangeResources,
  setValidation,
  customFields,
  tokens,
  anchorFields,
  onGoToOtherItem,
  newUxPrimaryAction,
  onCloseDialog,
  newUxOnDuplicate,
  notesPanel,
  newUxOnToggleDeactivate,
  initialSelectedActionId
}: {
  value: IAutomationTemplate;
  onChange: React.Dispatch<React.SetStateAction<IAutomationTemplate>>;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  onDelete?: (tpl: IAutomationTemplate) => void | undefined;
  expTpl: TaskBuilderContext;
  validation: AutomationBuilderActionValidationState;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  customFields: CustomUserField[];
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
  onGoToOtherItem?: (item: ITimelineItem) => void;
  newUxPrimaryAction?: React.ReactNode;
  onCloseDialog?: () => void;
  newUxOnDuplicate?: () => void;
  /** Timeline-item notes tab, supplied by the hosting dialog. */
  notesPanel?: React.ReactNode;
  /** Toggle deactivate/reactivate from the NEW_UX overflow menu (edit mode only). */
  newUxOnToggleDeactivate?: () => void;
  /**
   * Action to open on mount, e.g. the one that was selected in the read-only
   * viewer before switching to editing.
   */
  initialSelectedActionId?: string | null;
}) => {
  const { t } = useUnsafeTranslation();
  const newUxEnabled = useNewUx().enabled;
  const { actions } = value;

  const [viewMode, setViewMode] = useState<AutomationBuilderViewMode>(
    AUTOMATION_BUILDER_VIEW_MODES.settingsOverview
  );

  const [subPage, setSubPage] = useState<BlockSettingsSubPage | null>(null);
  const onBackFromSubPage = () => setSubPage(null);

  const [selectedActionId, setSelectedActionId] = useState<string | null>(
    initialSelectedActionId ?? null
  );
  const selectedAction = useMemo(
    () => actions.find((a) => a.id === selectedActionId),
    [actions, selectedActionId]
  );
  const anchorOptions = useGoogleCalendarEventInvitiationAnchorOptions(
    expTpl.anchor,
    anchorFields
  );

  useEffect(() => {
    const selectedActionIdIsNotVisibleAnymore =
      selectedActionId &&
      viewMode.mode === 'AUDIENCES_DETAIL' &&
      !viewMode.editModel.blocks.includes(selectedActionId);
    // NEW_UX removes the "preview" concept: a focused audience stays fully
    // editable, so we no longer clear the action selection for it. Classic keeps
    // the old preview-is-read-only behaviour.
    const isPreviewMode =
      !newUxEnabled &&
      viewMode.mode === 'AUDIENCES_OVERVIEW' &&
      viewMode.previewAudienceId;
    if (selectedActionIdIsNotVisibleAnymore || isPreviewMode) {
      setSelectedActionId(null);
    }
  }, [selectedActionId, viewMode, newUxEnabled]);

  const {
    onChangeActionOrder,
    onAddAction,
    onDeleteAction,
    onChangeAction,
    onDuplicateAction
  } = useMemo(() => {
    const tryToAddNewActionToViewModel = (nextActionId: string) => {
      if (viewMode.mode === 'AUDIENCES_DETAIL') {
        setViewMode(
          AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(
            {
              ...viewMode.editModel,
              blocks: [...viewMode.editModel.blocks, nextActionId]
            },
            viewMode.showHiddenBlocks
          )
        );
      }
    };

    const tryToRemoveActionFromViewModel = (removedActionId: string) => {
      if (viewMode.mode === 'AUDIENCES_DETAIL') {
        setViewMode(
          AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(
            {
              ...viewMode.editModel,
              blocks: viewMode.editModel.blocks.filter(
                (b) => b !== removedActionId
              )
            },
            viewMode.showHiddenBlocks
          )
        );
      }
    };

    const addAction = (
      v: IAutomationTemplate,
      nextAction: AutomationActionTemplate,
      insertIndex: number,
      onlyAddToTheseAudiences?: string[]
    ) => {
      const nextActions = [...v.actions];
      // The nextAction here uses a default defined in useActionDefinitions
      // We need to deal with special case for Google Calendar Event Invitations:
      // Unfortunately we don't know about all possible options at this point -
      // and we want the default to be one of the dynamic options.
      // We therefore hijack here - as here we have knowledge about all the options present.
      // This can lead to weird behaviour in case `addAction` is called with a specific
      // nextAction that is more than just the default.
      // This currently cannot be the case, so we just go with this solution for now...
      let actionToAdd = nextAction;
      if (
        actionToAdd.type ===
        AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION
      ) {
        if (anchorOptions.find((opt) => opt.type === 'AFTER_MILESTONE')) {
          actionToAdd = {
            ...actionToAdd,
            anchor: {
              type: 'AFTER_MILESTONE',
              userField: '',
              lowerBoundary: 0
            }
          };
        } else if (
          anchorOptions.find(
            (opt) => opt.type === 'AFTER_EXPERIENCE_START_DATE'
          )
        ) {
          actionToAdd = {
            ...actionToAdd,
            anchor: {
              type: 'AFTER_EXPERIENCE_START_DATE',
              lowerBoundary: 0
            }
          };
        }
      }
      nextActions.splice(insertIndex, 0, actionToAdd);
      tryToAddNewActionToViewModel(actionToAdd.id);
      return {
        ...v,
        actions: nextActions,
        audiences: v.audiences.map((a) => {
          if (
            onlyAddToTheseAudiences &&
            !onlyAddToTheseAudiences.includes(a.id)
          ) {
            return a;
          }
          return {
            ...a,
            blocks: [...a.blocks, actionToAdd.id]
          };
        })
      };
    };
    return {
      onChangeActionOrder: (sortedActionIds: string[]) => {
        onChange((v) => ({
          ...v,
          actions: sortBy(v.actions, (a) => sortedActionIds.indexOf(a.id))
        }));
      },
      onAddAction: (
        nextAction: AutomationActionTemplate,
        insertIndex: number,
        onlyAddToTheseAudiences?: string[]
      ) => {
        onChange((v) =>
          addAction(v, nextAction, insertIndex, onlyAddToTheseAudiences)
        );
      },
      onDeleteAction: (actionId: string) => {
        tryToRemoveActionFromViewModel(actionId);
        onChange((v) => ({
          ...v,
          actions: v.actions.filter((a) => a.id !== actionId),
          audiences: v.audiences.map((a) => ({
            ...a,
            blocks: a.blocks.filter((b) => b !== actionId)
          }))
        }));
      },

      onChangeAction: (nextAction: AutomationActionTemplate) =>
        onChange((v) => ({
          ...v,
          actions: v.actions.map((a) =>
            a.id === nextAction.id ? nextAction : a
          )
        })),
      onDuplicateAction: (actionId: string) => {
        onChange((v) => {
          const i = v.actions.findIndex((a) => a.id === actionId);
          if (i === -1) {
            return v;
          }

          const duplicate = {
            ...v.actions[i],
            id: shortid()
          };

          const audiencesForBlock =
            viewMode.mode === 'AUDIENCES_DETAIL'
              ? [] // only added to the view model within addBlock
              : v.audiences
                  .filter((a) => a.blocks.includes(actionId))
                  .map((a) => a.id);

          return addAction(v, duplicate, i + 1, audiencesForBlock);
        });
      }
    };
  }, [onChange, viewMode, anchorOptions]);
  const selectActionId = useCallback(
    (actionId: string | null) => {
      if (actionId === null && selectedAction) {
        setValidation((s) =>
          selectedAction
            ? validateAction(s, selectedAction, t, expTpl, value)
            : s
        );
      }
      setSelectedActionId(actionId);
    },
    [setValidation, selectedAction, t, expTpl, value]
  );
  const deselectAction = useCallback(() => {
    selectActionId(null);
  }, [selectActionId]);

  const actionDefs = useActionDefinitions();

  const dragDropContext = useRef<{
    currentlyVisibleActions: string[];
  }>({
    currentlyVisibleActions: []
  });

  const restrictAllActionOperations = !!(
    // NEW_UX: a focused audience stays fully editable (select / delete / reorder);
    // classic keeps the old preview-is-read-only behaviour.
    !newUxEnabled &&
    viewMode.mode === 'AUDIENCES_OVERVIEW' &&
    viewMode.previewAudienceId
  );
  const restrictActionResorting =
    restrictAllActionOperations ||
    (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks);

  const onPickActions = useCallback(
    (blocks: string[]) => {
      if (viewMode.mode !== 'AUDIENCES_DETAIL') {
        return undefined;
      }
      setViewMode(
        AUTOMATION_BUILDER_VIEW_MODES.audiencesDetail(
          { ...viewMode.editModel, blocks },
          viewMode.showHiddenBlocks
        )
      );
    },
    [viewMode]
  );

  return (
    <DragDropContext
      onDragEnd={(r) => {
        if (r.destination?.droppableId === DROP_IDS.actionOrder) {
          if (r.source.droppableId === DROP_IDS.actionOrder) {
            onChangeActionOrder(
              arrayMove(
                value.actions.map((b) => b.id),
                r.source.index,
                r.destination.index
              )
            );
          }

          const insertionI = findTrueInsertionIndex(
            dragDropContext.current.currentlyVisibleActions,
            value.actions.map((a) => a.id),
            r.destination.index
          );

          // While we're previewing an audience, a newly added block
          // is only applied to this particular audience.
          // However, we want a save step in between.
          // So we add it to NO audience, and just update the viewMode.editModel
          const restrictAudiences =
            viewMode.mode === 'AUDIENCES_DETAIL' ? [] : undefined;

          if (r.source.droppableId === DROP_IDS.actionsSelectorContent) {
            const newAction =
              actionDefs.find((b) => b.key === r.draggableId)?.emptyAction() ||
              null;
            if (newAction) {
              onAddAction(newAction, insertionI, restrictAudiences);
              setSelectedActionId(newAction.id);
            }
          }
        }
      }}
    >
      {newUxEnabled ? (
        <AutomationEditorNewUxShell
          ctx={{
            value,
            onChange,
            expTpl,
            tokens,
            viewMode,
            setViewMode,
            selectedAction: selectedAction ?? undefined,
            selectedActionId,
            selectActionId,
            deselectAction,
            onAddAction,
            onDeleteAction,
            onChangeAction,
            onDuplicateAction,
            onPickActions,
            actionDefs,
            dragDropContext,
            restrictAllActionOperations,
            restrictActionResorting,
            resources,
            onChangeResources,
            validation,
            setValidation,
            anchorFields,
            customFields,
            subPage,
            setSubPage,
            onBackFromSubPage,
            onGoToOtherItem,
            newUxPrimaryAction,
            newUxOnToggleDeactivate,
            onCloseDialog,
            onDelete,
            onDuplicate: newUxOnDuplicate,
            notesPanel
          }}
        />
      ) : (
      <Grid
        right={{
          min: SIDE_CONTAINER_MIN_WIDTH * 1.25,
          max: SIDE_CONTAINER_MAX_WIDTH * 1.3
        }}
      >
        <AutomationSettings
          value={value}
          onChange={onChange}
          onDelete={onDelete}
          expTpl={expTpl}
          viewMode={viewMode}
          setViewMode={setViewMode}
          customUserFields={customFields}
          tokens={tokens}
          onGoToOtherItem={onGoToOtherItem}
        />
        <MainContainer>
          <ActionList
            value={value}
            expTpl={expTpl}
            selectedAction={selectedActionId}
            onSelectAction={
              restrictAllActionOperations ? undefined : selectActionId
            }
            onDeleteAction={
              restrictAllActionOperations ? undefined : onDeleteAction
            }
            validation={validation}
            anchorFields={anchorFields}
            draggable={!restrictActionResorting}
            viewMode={viewMode}
            dragDropContext={dragDropContext}
            onPickActions={onPickActions}
            onDuplicateAction={onDuplicateAction}
            currentTemplateId={expTpl.templateId}
          />
        </MainContainer>
        <SideContainer>
          {subPage ? (
            <>
              <SideContainerHeader
                def={subPage.header.def}
                onBack={onBackFromSubPage}
              />
              {React.createElement(subPage.body, {
                onBack: onBackFromSubPage,
                resources,
                onChangeResources
              })}
            </>
          ) : selectedAction ? (
            <ActionSettings
              a={selectedAction}
              automation={value}
              onChange={onChangeAction}
              onBack={deselectAction}
              expTpl={expTpl}
              resources={resources}
              onChangeResources={onChangeResources}
              validation={validation[selectedAction.id] || null}
              setValidation={setValidation}
              anchorFields={anchorFields}
              tokens={tokens}
              customFields={customFields}
              goToSubPage={setSubPage}
              showOnCompleteHookSettings={true}
            />
          ) : (
            <ActionSelector />
          )}
        </SideContainer>
      </Grid>
      )}
    </DragDropContext>
  );
};

const ActionSettingsInnerReadOnly = ({
  value,
  expTpl,
  anchorFields,
  resources
}: {
  value: AutomationActionTemplate;
  expTpl: TaskBuilderContext;
  anchorFields: AnchorField[];
  resources: IExperienceTemplateResource[];
}) => {
  switch (value.type) {
    case AutomationActionType.ASANA_CREATE_TASK:
      return (
        <ActionSettingsAsanaCreateTaskReadOnly value={value} expTpl={expTpl} />
      );
    case AutomationActionType.ATLASSIAN_CREATE_ISSUE:
      return (
        <ActionSettingsAtlassianCreateIssueReadOnly
          value={value}
          expTpl={expTpl}
        />
      );
    case AutomationActionType.ENROLL_IN_EXPERIENCE:
      return (
        <ActionSettingsEnrollInExperienceReadOnly
          value={value}
          expTpl={expTpl}
        />
      );
    case AutomationActionType.GOOGLE_ADD_TO_GROUP:
      return (
        <ActionSettingsGoogleAddToGroupReadOnly value={value} expTpl={expTpl} />
      );
    case AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION:
      return (
        <ActionSettingsGoogleCalendarEventInvitationReadOnly
          value={value}
          expTpl={expTpl}
          anchorFields={anchorFields}
        />
      );
    case AutomationActionType.DOCEBO_ENROLL:
      return (
        <ActionSettingsDoceboEnrollReadOnly value={value} expTpl={expTpl} />
      );
    case AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT:
      return (
        <ActionSettingsLessonlyAssignToContentReadOnly
          value={value}
          expTpl={expTpl}
        />
      );
    case AutomationActionType.SLACK_INVITE_TO_CHANNEL:
      return (
        <ActionSettingsSlackInviteToChannelReadOnly
          value={value}
          expTpl={expTpl}
        />
      );
    case AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION:
      return (
        <ActionSettingsMicrosoftCalendarEventInvitationReadOnly
          value={value}
          expTpl={expTpl}
          anchorFields={anchorFields}
        />
      );
    case AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP:
      return (
        <ActionSettingsMicrosoftAddToGroupReadOnly
          value={value}
          expTpl={expTpl}
        />
      );
    case AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL:
      return (
        <ActionSettingsMicrosoftTeamsInviteToChannelReadOnly
          value={value}
          expTpl={expTpl}
        />
      );
    case AutomationActionType.OKTA_ADD_TO_GROUP:
      return (
        <ActionSettingsOktaAddToGroupReadOnly value={value} expTpl={expTpl} />
      );
    case AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE:
      return (
        <ActionSettingsCreateDocumentFromTemplateReadOnly
          value={value}
          expTpl={expTpl}
          resources={resources}
        />
      );
    case AutomationActionType.HTTP_POST:
      return <ActionSettingsHttpPostReadOnly value={value} expTpl={expTpl} />;
    case AutomationActionType.SYNC_TO_DATA_DESTINATION:
      return (
        <ActionSettingsSyncToDataDestinationReadOnly
          value={value}
          expTpl={expTpl}
          resources={resources}
        />
      );
    case AutomationActionType.WORKDAY_TRIGGER_BUSINESS_PROCESS:
      return <ActionSettingsWorkdayReadOnly value={value} />;
    case AutomationActionType.UNENROLL_FROM_EXPERIENCE:
      return null;
    default:
      return neverRender(value);
  }
};

const ActionSettingsReadOnly = ({
  a,
  expTpl,
  anchorFields,
  onBack,
  resources
}: {
  a: AutomationActionTemplate;
  expTpl: TaskBuilderContext;
  anchorFields: AnchorField[];
  resources: IExperienceTemplateResource[];
  onBack: () => void;
}) => {
  const def = useActionDefinition(a);
  return (
    <SideContainer>
      <SideContainerHeader def={def} onBack={onBack} />
      <ActionSettingsInnerReadOnly
        value={a}
        expTpl={expTpl}
        anchorFields={anchorFields}
        resources={resources}
      />
    </SideContainer>
  );
};

const AutomationBuilderReadOnlyInner = ({
  value,
  expTpl,
  anchorFields,
  validation,
  tokens,
  onGoToOtherItem,
  resources,
  onSelectedActionChange
}: {
  value: IAutomationTemplate;
  expTpl: TaskBuilderContext;
  anchorFields: AnchorField[];
  validation: AutomationBuilderActionValidationState;
  tokens: TokenDefinition[];
  onGoToOtherItem?: (nextItem: ITimelineItem) => void;
  resources: IExperienceTemplateResource[];
  /**
   * Reports the action selected in the viewer, so the hosting dialog can carry
   * it over to the editor when the user switches to editing.
   */
  onSelectedActionChange?: (actionId: string | null) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { actions } = value;
  const [selectedActionId, setSelectedActionId] = useState<string | null>(
    value.actions[0]?.id || null
  );
  useEffect(() => {
    onSelectedActionChange?.(selectedActionId);
  }, [selectedActionId, onSelectedActionChange]);
  const deselectAction = useCallback(() => {
    setSelectedActionId(null);
  }, [setSelectedActionId]);

  const selectedAction = useMemo(
    () => actions.find((a) => a.id === selectedActionId) || null,
    [actions, selectedActionId]
  );

  return (
    <DragDropContext onDragEnd={NOOP}>
      <Grid>
        <AutomationSettingsOverview
          value={value}
          expTpl={expTpl}
          tokens={tokens}
          onGoToOtherItem={onGoToOtherItem}
        />
        <MainContainer>
          <ActionList
            value={value}
            expTpl={expTpl}
            selectedAction={selectedActionId}
            onSelectAction={setSelectedActionId}
            validation={validation}
            anchorFields={anchorFields}
            draggable={false}
            viewMode={AUTOMATION_BUILDER_VIEW_MODES.settingsOverview}
            currentTemplateId={expTpl.templateId}
          />
        </MainContainer>
        {selectedAction ? (
          <ActionSettingsReadOnly
            a={selectedAction}
            expTpl={expTpl}
            onBack={deselectAction}
            anchorFields={anchorFields}
            resources={resources}
          />
        ) : (
          <SideSettingsReadOnlyNoSelection>
            {t('Select an action to view details')}
          </SideSettingsReadOnlyNoSelection>
        )}
      </Grid>
    </DragDropContext>
  );
};

// AI-generated experiences can carry malformed automation data (e.g. a null
// srcTemplate or an unknown action type). We guard the known cases at the source
// (getActionDefKey / getPublicUrl / neverRender), but also wrap both builders in
// an error boundary so any UNFORESEEN render crash is contained to the automation
// panel — and, crucially, gives the user a way OUT (retry, or delete the broken
// automation) instead of a dead "something went wrong".
const AutomationErrorContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 24px;
  text-align: center;
`;

const AutomationErrorFallback = ({
  onDelete,
  onRetry
}: {
  onDelete?: () => void;
  onRetry: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <AutomationErrorContainer>
      <Typography variant="h4">
        {t("This automation couldn't be displayed")}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {t(
          'Its configuration is incomplete or invalid — this can happen with automations created by the assistant. Try again, or remove it and add a new one.'
        )}
      </Typography>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button variant="outlined" onClick={onRetry}>
          {t('Try again')}
        </Button>
        {onDelete && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Trash2 size={16} />}
            onClick={onDelete}
          >
            {t('Delete automation')}
          </Button>
        )}
      </div>
    </AutomationErrorContainer>
  );
};

const withAutomationErrorBoundary = <
  P extends {
    value: IAutomationTemplate;
    onDelete?: (tpl: IAutomationTemplate) => void | undefined;
  }
>(
  Inner: React.ComponentType<P>
) => {
  const Wrapped = (props: P) => (
    <ErrorBoundary
      fallbackRender={({ resetErrorBoundary }) => (
        <AutomationErrorFallback
          onRetry={resetErrorBoundary}
          onDelete={
            props.onDelete ? () => props.onDelete?.(props.value) : undefined
          }
        />
      )}
    >
      <Inner {...props} />
    </ErrorBoundary>
  );
  return Wrapped;
};

export const AutomationBuilder = withAutomationErrorBoundary(
  AutomationBuilderInner
);
export const AutomationBuilderReadOnly = withAutomationErrorBoundary(
  AutomationBuilderReadOnlyInner
);

type AutomationViewerRail =
  | 'actions'
  | 'audience'
  | 'timing'
  | 'recipients'
  | 'notes';

/**
 * NEW_UX read-only automation viewer (Layout A shell, view mode). Mirrors
 * AutomationEditorNewUxShell but read-only, and adds the Recipients + Notes
 * rails (supplied as nodes by the hosting dialog so this stays decoupled from
 * pages/Details). Reskin only — re-houses the SAME read-only content the classic
 * tabbed automation dialog shows (settings summary + action list + People +
 * Notes) into the icon rail. Rendered by AutomationTemplateReadOnlyDialog when
 * useNewUx().enabled.
 *
 * Field→panel mapping mirrors the automation editor split:
 *   Actions    = Admin Name + action list (preview) / read-only action settings
 *   Audience   = Segments (clickable → preview)
 *   Timing     = Trigger (+ catch-up)
 *   Recipients = People execution report (node prop)
 *   Notes      = Timeline-item notes (node prop)
 */
const AutomationViewerNewUxShellInner = ({
  value,
  expTpl,
  anchorFields,
  validation,
  tokens,
  resources,
  onGoToOtherItem,
  recipientsPanel,
  notesPanel,
  editButton,
  onCloseDialog,
  onSelectedActionChange
}: {
  value: IAutomationTemplate;
  expTpl: TaskBuilderContext;
  anchorFields: AnchorField[];
  validation: AutomationBuilderActionValidationState;
  tokens: TokenDefinition[];
  resources: IExperienceTemplateResource[];
  onGoToOtherItem?: (nextItem: ITimelineItem) => void;
  /** People / execution-report tab, supplied by the hosting dialog. */
  recipientsPanel?: React.ReactNode;
  /** Timeline-item notes tab, supplied by the hosting dialog. */
  notesPanel?: React.ReactNode;
  /** "Edit automation" primary action (permission-gated), supplied by the dialog. */
  editButton?: React.ReactNode;
  /** Close the hosting dialog (top-bar ✕). */
  onCloseDialog?: () => void;
  /**
   * Reports the action selected in the viewer, so the hosting dialog can carry
   * it over to the editor when the user switches to editing.
   */
  onSelectedActionChange?: (actionId: string | null) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const segmentRuleConfig = useUserRuleConfigWithAutocompletion(
    org.id,
    org.customUserFields,
    expTpl.tz,
    { orgId: org.id, experienceTemplateId: expTpl.templateId }
  );
  const [rail, setRail] = useState<AutomationViewerRail>('actions');
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [previewAudienceId, setPreviewAudienceId] = useState<string | null>(
    null
  );
  const [selectedActionId, setSelectedActionId] = useState<string | null>(
    value.actions[0]?.id || null
  );
  useEffect(() => {
    onSelectedActionChange?.(selectedActionId);
  }, [selectedActionId, onSelectedActionChange]);
  const selectedAction = useMemo(
    () => value.actions.find((a) => a.id === selectedActionId) || null,
    [value.actions, selectedActionId]
  );

  const viewMode: AutomationBuilderViewMode = previewAudienceId
    ? AUTOMATION_BUILDER_VIEW_MODES.audiencesOverview(previewAudienceId)
    : AUTOMATION_BUILDER_VIEW_MODES.settingsOverview;

  const segments = value.audiences.filter((a) => a.rule !== 'EVERYONE');

  const openRail = (next: AutomationViewerRail) => {
    setRailCollapsed(false);
    setSelectedActionId(null);
    setRail(next);
  };
  const goRail = (next: AutomationViewerRail) => {
    if (next === rail && !railCollapsed) {
      setRailCollapsed(true);
      return;
    }
    openRail(next);
  };

  const renderActions = () => (
    <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
      <UX.PanelHeader icon={<Zap size={17} />} title={t('Actions')} />
      <ViewerField label={t('Admin name')}>
        <PlainTextWithReplacementPlaceholders
          text={value.title}
          tokens={tokens}
        />
      </ViewerField>
    </div>
  );

  const renderAudience = () => (
    <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
      <UX.PanelHeader icon={<UserCheck size={17} />} title={t('Audience')} />
      <ViewerSegmentList
        audiences={value.audiences}
        previewAudienceId={previewAudienceId}
        onPreview={(id) =>
          setPreviewAudienceId((cur) => (cur === id ? null : id))
        }
        itemTypeLabel={t('automation')}
        ruleConfig={segmentRuleConfig}
      />
    </div>
  );

  const renderTiming = () => (
    <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
      <UX.PanelHeader icon={<Clock size={17} />} title={t('Timing & Triggers')} />
      <AutomationViewerTrigger
        value={value}
        allTasks={expTpl.tasks}
        onGoToOtherItem={onGoToOtherItem}
        ruleConfig={segmentRuleConfig}
        anchor={expTpl.anchor}
        tz={expTpl.tz}
      />
    </div>
  );

  const renderInspector = () => {
    if (selectedAction) {
      return (
        <ActionSettingsReadOnly
          a={selectedAction}
          expTpl={expTpl}
          onBack={() => setSelectedActionId(null)}
          anchorFields={anchorFields}
          resources={resources}
        />
      );
    }
    if (rail === 'audience') return renderAudience();
    if (rail === 'timing') return renderTiming();
    return renderActions();
  };

  const isFullWidthRail = rail === 'recipients' || rail === 'notes';

  return (
    <DragDropContext onDragEnd={NOOP}>
      <UX.EditorShell>
        <UX.SectionRailContainer>
          <UX.RailButton
            active={rail === 'actions'}
            title={t('Actions')}
            onClick={() => goRail('actions')}
          >
            <Zap size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'audience'}
            title={t('Audience')}
            onClick={() => goRail('audience')}
          >
            <UserCheck size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'timing'}
            title={t('Timing & triggers')}
            onClick={() => goRail('timing')}
          >
            <Clock size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'recipients'}
            title={t('Schedule')}
            onClick={() => goRail('recipients')}
          >
            <Calendar size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'notes'}
            title={t('Notes')}
            onClick={() => goRail('notes')}
          >
            <FilePen size={20} />
          </UX.RailButton>
        </UX.SectionRailContainer>
        <UX.RightColumn>
          <UX.TopBar>
            <UX.TopBarHeading>
              <UX.TopBarCrumb title={expTpl.title}>{expTpl.title}</UX.TopBarCrumb>
              <UX.TopBarTitle>
                <UX.TopBarTitleText title={value.title || t('Automation')}>
                  {value.title || t('Automation')}
                </UX.TopBarTitleText>
              </UX.TopBarTitle>
            </UX.TopBarHeading>
            <UX.TopBarSpacer />
            <UX.TopBarRight>
              {editButton}
              {onCloseDialog && (
                <IconButton
                  size="small"
                  aria-label={t('Close')}
                  onClick={onCloseDialog}
                >
                  <X size={18} />
                </IconButton>
              )}
            </UX.TopBarRight>
          </UX.TopBar>
          <UX.EditorBody>
            {isFullWidthRail ? (
              <NewUxViewerFullWidth>
                <NewUxFullWidthPanel>
                  <NewUxFullWidthPanelHead>
                    <UX.PanelHeader
                      icon={
                        rail === 'recipients' ? (
                          <Calendar size={17} />
                        ) : (
                          <FilePen size={17} />
                        )
                      }
                      title={rail === 'recipients' ? t('Schedule') : t('Notes')}
                    />
                  </NewUxFullWidthPanelHead>
                  <NewUxFullWidthPanelBody>
                    {rail === 'recipients' ? recipientsPanel : notesPanel}
                  </NewUxFullWidthPanelBody>
                </NewUxFullWidthPanel>
              </NewUxViewerFullWidth>
            ) : (
              <>
                <NewUxAutomationColumn collapsed={railCollapsed}>
                  {renderInspector()}
                </NewUxAutomationColumn>
                <UX.PreviewCanvas onBackgroundClick={() => setSelectedActionId(null)}>
                  <div style={{ width: '100%', maxWidth: 720, margin: '0 auto' }}>
                    <UX.AudienceTabs
                      allLabelEveryone={t('All – sends to everyone')}
                allLabelBlocks={t('All blocks')}
                      everyone={{
                        id: getEveryoneAudience(value.audiences).id,
                        label: getAudienceName(getEveryoneAudience(value.audiences), true, t),
                        empty:
                          getEveryoneAudience(value.audiences).blocks.length ===
                          0
                      }}
                      segments={segments.map((a) => ({
                        id: a.id,
                        label: a.name || t('Segment'),
                        color: a.color || UX.tokens.color.blue500,
                        empty: a.blocks.length === 0
                      }))}
                      activeId={previewAudienceId}
                      onSelect={(id) => {
                        setSelectedActionId(null);
                        setPreviewAudienceId(id);
                        if (id) {
                          setRailCollapsed(false);
                          setRail('audience');
                        }
                      }}
                    />
                    <ActionList
                      value={value}
                      expTpl={expTpl}
                      selectedAction={selectedActionId}
                      onSelectAction={setSelectedActionId}
                      validation={validation}
                      anchorFields={anchorFields}
                      draggable={false}
                      viewMode={viewMode}
                      currentTemplateId={expTpl.templateId}
                      newUx
                    />
                  </div>
                </UX.PreviewCanvas>
              </>
            )}
          </UX.EditorBody>
        </UX.RightColumn>
      </UX.EditorShell>
    </DragDropContext>
  );
};

export const AutomationViewerNewUxShell = withAutomationErrorBoundary(
  AutomationViewerNewUxShellInner
);
